import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

export interface MeteorsProps {
  number?: number;
  minDelay?: number;
  maxDelay?: number;
  minDuration?: number;
  maxDuration?: number;
  minPause?: number;
  maxPause?: number;
  className?: string;
}

interface MeteorState {
  left: number;
  top: number;
  angleDeg: number;
  dx: number;
  dy: number;
  duration: number;
  tail: number;
  cycle: number;
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function createMeteorState(minDuration: number, maxDuration: number): MeteorState {
  const angleDeg = rand(126, 154);
  const rad = (angleDeg * Math.PI) / 180;
  const travel = rand(100, 190);
  const dx = Math.cos(rad) * travel;
  const dy = Math.sin(rad) * travel;

  return {
    left: rand(8, 94),
    top: rand(2, 38),
    angleDeg,
    dx,
    dy,
    duration: rand(minDuration, maxDuration),
    tail: rand(28, 50),
    cycle: 0,
  };
}

function MeteorSlot({
  initialDelay,
  minDuration,
  maxDuration,
  minPause,
  maxPause,
  className,
}: {
  initialDelay: number;
  minDuration: number;
  maxDuration: number;
  minPause: number;
  maxPause: number;
  className?: string;
}) {
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [meteor, setMeteor] = useState<MeteorState | null>(null);

  const scheduleNext = useCallback(() => {
    if (pauseTimerRef.current) {
      clearTimeout(pauseTimerRef.current);
    }

    pauseTimerRef.current = setTimeout(() => {
      setMeteor((current) => {
        const next = createMeteorState(minDuration, maxDuration);
        next.cycle = (current?.cycle ?? 0) + 1;
        return next;
      });
    }, rand(minPause, maxPause) * 1000);
  }, [minDuration, maxDuration, minPause, maxPause]);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setMeteor(createMeteorState(minDuration, maxDuration));
    }, initialDelay * 1000);

    return () => {
      clearTimeout(startTimer);
      if (pauseTimerRef.current) {
        clearTimeout(pauseTimerRef.current);
      }
    };
  }, [initialDelay, minDuration, maxDuration]);

  if (!meteor) return null;

  return (
    <span
      key={meteor.cycle}
      className={['auth-meteor', className].filter(Boolean).join(' ')}
      style={
        {
          left: `${meteor.left}%`,
          top: `${meteor.top}%`,
          ['--meteor-angle' as string]: `${meteor.angleDeg}deg`,
          ['--meteor-duration' as string]: `${meteor.duration}s`,
          ['--meteor-dx' as string]: `${meteor.dx}px`,
          ['--meteor-dy' as string]: `${meteor.dy}px`,
          ['--meteor-tail' as string]: `${meteor.tail}px`,
        } as CSSProperties
      }
      onAnimationEnd={() => {
        setMeteor(null);
        scheduleNext();
      }}
    />
  );
}

/** Meteor shower — each streak spawns at a new random point after a random pause. */
export function Meteors({
  number = 4,
  minDelay = 0,
  maxDelay = 12,
  minDuration = 4.5,
  maxDuration = 8.5,
  minPause = 6,
  maxPause = 22,
  className,
}: MeteorsProps) {
  const slotSpacing = (maxDelay - minDelay) / Math.max(number, 1);
  const initialDelays = Array.from({ length: number }, (_, id) =>
    minDelay + id * slotSpacing + rand(0, slotSpacing * 0.4),
  );

  return (
    <>
      {initialDelays.map((initialDelay, id) => (
        <MeteorSlot
          key={id}
          initialDelay={initialDelay}
          minDuration={minDuration}
          maxDuration={maxDuration}
          minPause={minPause}
          maxPause={maxPause}
          className={className}
        />
      ))}
    </>
  );
}
